import { faCalendar, faClock, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isPast } from 'date-fns';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import RenderRichText from '@/components/RenderRichText';
import { Color } from '@/constants/color';
import defaultTimeZone from '@/constants/defaultTimeZone';
import { useMembersContext } from '@/contexts/membersContext';
import useTranslate from '@/hooks/useTranslate';
import GradientBg from '@/pages/members/pages/Onboarding/components/GradientBg';
import Alert from '@/ui/members/components/Alert';
import Button from '@/ui/members/components/Button';
import Heading from '@/ui/members/components/Heading';
import Spinner from '@/ui/members/components/Spinner';

import { DownloadToGetStarted } from '../../components/DownloadToGetStarted';
import { useAppointment } from '../../hooks/useAppointments';
import { Status } from '../../types';

const ALLOW_JOIN_BEFORE_MINUTES = 15;

const MeetingDetails = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [searchParams] = useSearchParams();
  const {
    memberData: { person }
  } = useMembersContext();

  const isEnded = searchParams.get('ended') === 'true';

  const { data: event, isPending: isLoading } = useAppointment(appointmentId);

  const isAppointmentJoinable = useMemo(
    () =>
      !!event &&
      isPast(
        new Date(new Date(event.appointment.start_at).getTime() - ALLOW_JOIN_BEFORE_MINUTES * 60000)
      ),
    [event]
  );

  const hasAppointmentEnded = useMemo(
    () => !!event && isPast(new Date(event.appointment.end_at)),
    [event]
  );

  const [timeToStart, setTimeToStart] = useState<string | null>(null);

  useEffect(() => {
    if (!event) {
      setTimeToStart(null);
      return;
    }

    const update = () => {
      const now = new Date();
      const startTime = new Date(event.appointment.start_at);
      const diff = startTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeToStart(null);
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      let str = '';
      if (hours > 0) str += `${hours}:`;
      if (minutes > 0 || hours > 0)
        str += `${minutes.toString().padStart(hours > 0 ? 2 : 1, '0')}:`;
      str += seconds.toString().padStart(2, '0');

      setTimeToStart(str);
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [event]);

  const isAppointmentOngoing = useMemo(() => {
    if (!event) return false;
    const now = new Date();
    const startTime = new Date(event.appointment.start_at);
    return !hasAppointmentEnded && now >= startTime;
  }, [event, hasAppointmentEnded]);

  const isZoomButtonDisabled = useMemo(() => {
    if (!event) return true;
    if (hasAppointmentEnded) return true;
    if (!isAppointmentJoinable) return true;
    if (!event.appointment.zoom_join_url) return true;
    return false;
  }, [event, hasAppointmentEnded, isAppointmentJoinable]);

  const showJoinButton = useMemo(() => {
    if (!event) return false;
    if (event.state === Status.CANCELLED) return false;
    if (event.state === Status.RESCHEDULED) return false;
    return true;
  }, [event]);

  const { t } = useTranslate('pages.members.pages.MeetingDetails');

  const joinMeetingButtonText = useMemo(() => {
    if (!event) return t('loading');

    switch (true) {
      case event.state === Status.RESCHEDULED:
        return t('rescheduledMessage');
      case event.state === Status.CANCELLED:
        return t('cancelledMessage');
      case !event.appointment.zoom_join_url:
        return t('missingZoomLink');
      case hasAppointmentEnded:
        return t('meetingEnded');
      case isAppointmentOngoing:
        return t('joinMeeting');
      case !!timeToStart:
        return t('meetingIn', { time: timeToStart });
      default:
        return t('joinMeeting');
    }
  }, [event, hasAppointmentEnded, isAppointmentOngoing, timeToStart, t]);

  const handleJoinZoom = () => {
    navigate('meeting');
  };

  if (!appointmentId)
    return (
      <Alert level="error" dismissable={false}>
        {t('noAppointmentId')}
      </Alert>
    );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  const hasAgentName = !!event?.agent?.first_name && !!event?.agent?.last_name;

  return (
    <div className="flex flex-col">
      <div className="pt-10 dark:bg-gray-800 light:bg-gray-800 w-full flex flex-col items-center pb-20">
        <Heading
          value={1}
          text={isEnded ? t('youLeftTitle') : t('pageTitle')}
          className="mb-2 font-normal text-5xl text-center mx-4"
        />
        {!isEnded && (
          <Heading
            value={3}
            text={t('pageSubTitle')}
            className="mb-6 font-normal light:text-gray-400 dark:text-gray-400 text-center mx-4"
          />
        )}

        {event ? (
          <div className="flex flex-col max-w-5xl w-full px-6 md:px-0">
            <div className="mb-6">
              {event.state === Status.CANCELLED && (
                <Alert dismissable={false} level="warn">
                  {t('cancelledMessage')}
                </Alert>
              )}
              {event.state === Status.RESCHEDULED && (
                <Alert dismissable={false} level="info">
                  {t('rescheduledMessage')}
                </Alert>
              )}
            </div>
            <GradientBg imgClassName="hidden md:block">
              <div className="p-6 dark:bg-gray-900 light:bg-gray-900 rounded-lg z-10">
                <div className="flex flex-col md:flex-row md:space-x-20 md:items-stretch">
                  <div className="flex flex-col md:flex-1">
                    <Heading value={1} text={event.appointment.title} className="mb-1" />
                    {hasAgentName && (
                      <p className="text-gray-400 mb-4">
                        {event.agent.first_name} {event.agent.last_name}
                      </p>
                    )}

                    <div className="space-y-4 mb-6">
                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={faCalendar}
                          className="text-gray-400 mt-1 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <p className="text-base text-gray-400">
                            {new Date(event.appointment.start_at).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                              weekday: 'long',
                              timeZone: person.time_zone || defaultTimeZone
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={faClock}
                          className="text-gray-400 mt-1 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <p className="text-base text-gray-400">
                            {new Date(event.appointment.start_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                              timeZone: person.time_zone || defaultTimeZone
                            })}
                            {' - '}
                            {new Date(event.appointment.end_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                              timeZone: person.time_zone || defaultTimeZone
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={faGlobe}
                          className="text-gray-400 mt-1 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <p className="text-base text-gray-400">
                            Time zone -{' '}
                            {person.time_zone
                              ? person.time_zone.replace('_', ' ')
                              : defaultTimeZone}
                          </p>
                        </div>
                      </div>
                    </div>

                    {event.appointment.description && (
                      <div className="md:hidden mb-6">
                        <h3 className="font-semibold text-lg mb-2">Description</h3>
                        <RenderRichText
                          body={event.appointment.description}
                          className="prose dark:prose-invert prose-sm"
                        />
                      </div>
                    )}

                    {showJoinButton && (
                      <div className="mt-3">
                        <Button
                          onClick={handleJoinZoom}
                          disabled={isZoomButtonDisabled}
                          className="w-full dark:bg-uniform-green-800 py-4"
                          color={Color.GREEN}>
                          {joinMeetingButtonText}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="hidden md:flex md:flex-col md:flex-shrink-0 light:bg-gray-800 dark:bg-gray-800 px-6 py-3 rounded-lg w-lg ">
                    {event.appointment.description && (
                      <div className="mt-2">
                        <h3 className="font-semibold text-lg mb-2">Description</h3>
                        <RenderRichText
                          body={event.appointment.description}
                          className="prose dark:prose-invert prose-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </GradientBg>
          </div>
        ) : !isEnded ? (
          <Alert level="error" dismissable={false}>
            {t('appointmentNotFound')}
          </Alert>
        ) : null}
      </div>

      <div className="flex-1 dark:bg-gray-900 light:bg-gray-900 w-full z-100 pt-10 flex flex-col items-center justify-center grow">
        <DownloadToGetStarted />
      </div>
    </div>
  );
};

export default MeetingDetails;
